import styled, { css } from 'styled-components';
import ReactDayPicker from 'react-day-picker';
import { always } from 'ramda';
import 'react-day-picker/lib/style.css';

import { smallSpacedHeading, spacedHeading } from '../../../../theme/typography';
import { themeColor, whenThemePropEqualsTrue, themeBorder } from '../../../../theme/getters';
import { Icon } from '../../../../shared/components/icon';
import { media } from '../../../../theme/media';
import { square } from '../../../../theme/mixins';

export const DayPicker = styled(ReactDayPicker)`
  width: 100%;

  * {
    outline: none;
  }

  .DayPicker-Month {
    width: 100%;
    margin: 0;
    border-collapse: separate;
  }

  .DayPicker-WeekdaysRow {
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    justify-content: space-between;
    margin-bottom: 40px;

    ${media.tablet`
      display: table-row;
      margin-bottom: 0;
    `}
  }

  .DayPicker-Week {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    width: 100%;

    ${media.tablet`
      display: table-row;
    `}

    .DayPicker-Day {
      position: relative;
      flex-basis: calc(${100 / 7}% - 10px);
      margin: 5px;
      box-sizing: border-box;

      ${media.tablet`
        margin: auto;
        padding: 8px 12px;
      `}

      &:before {
        content: '';
        display: block;
        padding-top: 100%;

        ${media.tablet`
          display: none;
        `}
      }
    }
  }

  .DayPicker-Day {
    padding: 0;
    background-color: transparent !important;
    -webkit-tap-highlight-color: transparent;

    ${media.tablet`
      padding: 8px 12px;
    `}
  }
`;

export const Navbar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 0 5px 32px;

  ${media.tablet`
    margin: 0 7px 56px;
  `}
`;

export const NavIcon = styled(Icon).attrs(() => ({ size: 32 }))`
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  ${props =>
    props.inactive &&
    css`
      pointer-events: none;
    `}
`;

export const MonthTitle = styled.div`
  ${spacedHeading};
`;

export const Weekday = styled.div`
  ${smallSpacedHeading};
  color: ${themeColor('primary')} !important;

  font-size: 12px !important;
  flex-basis: calc(${100 / 7}% - 10px);
  margin: 5px;
  box-sizing: border-box;
  padding: 0 !important;

  ${media.tablet`
    font-size: 0.875em !important;
    padding: 0 0 40px !important;
  `}
`;

const whenIsDisabled = whenThemePropEqualsTrue('disabled');
const whenIsSelected = whenThemePropEqualsTrue('selected');
const whenIsToday = whenThemePropEqualsTrue('today');

const getDisabled = whenIsDisabled(
  () => css`
    border: transparent;
    color: ${themeColor('disabled')};
  `
);

const getSelected = whenIsSelected(
  () => css`
    color: ${themeColor('white')};
    background-color: ${themeColor('primary')};
    border: 1px solid ${themeColor('primary')};
  `
);

const getDisabledTodayBackgroundColor = whenIsDisabled(
  always(
    css`
      background-color: ${themeColor('disabled')};
    `
  )
);

const getSelectedTodayBackgroundColor = whenIsSelected(
  always(
    css`
      background-color: ${themeColor('white')};
    `
  )
);

const getToday = whenIsToday(
  () => css`
    border: transparent;
    font-weight: normal;

    &::after {
      content: '';
      position: absolute;
      bottom: 4px;
      left: calc(50% - 2px);
      height: 4px;
      width: 4px;
      background-color: ${themeColor('primary')};
      ${getDisabledTodayBackgroundColor}
      ${getSelectedTodayBackgroundColor}
      border-radius: 100%;

      ${media.tablet`
        top: 32px;
      `}
    }
  `
);

export const Day = styled.div`
  ${smallSpacedHeading};
  flex: 1;
  border: ${themeBorder('regular')};
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
  ${square('100%')};
  position: absolute;
  top: 0;
  left: 0;
  
  ${media.tablet`
    position: relative;
    ${square('40px')};
  `}
  
  ${getDisabled}
  ${getSelected}
  ${getToday}
`;
